import type { ChangeEvent } from 'react';
import type { ConstructionType, StateCode } from '@zg/shared';
import { FieldGrid } from '../form/FieldGrid';
import { TextField } from '../form/TextField';
import { Select } from '../form/Select';
import type { UseIndicationFormResult } from '../../hooks/useIndicationForm';
import { OtherStatePanel } from './OtherStatePanel';

interface Props {
  form: UseIndicationFormResult;
}

const constructionOptions: { value: ConstructionType; label: string }[] = [
  { value: 'Frame', label: 'Frame' },
  { value: 'JoistedMasonry', label: 'Joisted Masonry' },
  { value: 'NonCombustible', label: 'Non-Combustible' },
  { value: 'FireResistive', label: 'Fire Resistive' },
];

const stateOptions: { value: StateCode; label: string }[] = [
  { value: 'MI', label: 'Michigan' },
  { value: 'GA', label: 'Georgia' },
  { value: 'FL', label: 'Florida' },
  { value: 'TX', label: 'Texas' },
  { value: 'CA', label: 'California' },
  { value: 'OTHER', label: 'Other (request custom indication)' },
];

function intFromEvent(e: ChangeEvent<HTMLInputElement>): number | undefined {
  const v = e.target.valueAsNumber;
  if (!Number.isFinite(v)) return undefined;
  return Math.trunc(v);
}

export function StepProjectInfo({ form }: Props) {
  const { state, setField, markTouched, errors } = form;

  return (
    <fieldset className="border-0 p-0 m-0">
      <legend className="sr-only">Project information</legend>
      <FieldGrid>
        <div data-field="namedInsured" className="md:col-span-2">
          <TextField
            label="Named Insured"
            required
            value={state.namedInsured}
            onChange={(e) => setField('namedInsured', e.target.value)}
            onBlur={() => markTouched('namedInsured')}
            error={errors.namedInsured}
            autoComplete="organization"
          />
        </div>

        <div data-field="projectAddress" className="md:col-span-2">
          <TextField
            label="Project Address"
            required
            value={state.projectAddress}
            onChange={(e) => setField('projectAddress', e.target.value)}
            onBlur={() => markTouched('projectAddress')}
            error={errors.projectAddress}
            autoComplete="street-address"
          />
        </div>

        <div data-field="cityStateZip" className="md:col-span-2">
          <TextField
            label="City, State, Zip"
            required
            hint="e.g. Atlanta, GA 30310"
            value={state.cityStateZip}
            onChange={(e) => setField('cityStateZip', e.target.value)}
            onBlur={() => markTouched('cityStateZip')}
            error={errors.cityStateZip}
          />
        </div>

        <div data-field="constructionType">
          <Select
            label="Construction Type"
            required
            placeholder="Select one"
            value={state.constructionType ?? ''}
            onChange={(e) =>
              setField(
                'constructionType',
                (e.target.value as ConstructionType) || undefined,
              )
            }
            onBlur={() => markTouched('constructionType')}
            options={constructionOptions}
            error={errors.constructionType}
          />
        </div>

        <div data-field="state">
          <Select
            label="State"
            required
            placeholder="Select a state"
            value={state.state ?? ''}
            onChange={(e) =>
              setField('state', (e.target.value as StateCode) || undefined)
            }
            onBlur={() => markTouched('state')}
            options={stateOptions}
            error={errors.state}
          />
        </div>

        <div data-field="numberOfBuildings">
          <TextField
            label="Number of Buildings"
            type="number"
            min={1}
            step={1}
            numeric
            required
            value={state.numberOfBuildings ?? ''}
            onChange={(e) => setField('numberOfBuildings', intFromEvent(e))}
            onBlur={() => markTouched('numberOfBuildings')}
            error={errors.numberOfBuildings}
          />
        </div>

        <div data-field="numberOfStories">
          <TextField
            label="Number of Stories"
            type="number"
            min={1}
            step={1}
            numeric
            required
            value={state.numberOfStories ?? ''}
            onChange={(e) => setField('numberOfStories', intFromEvent(e))}
            onBlur={() => markTouched('numberOfStories')}
            error={errors.numberOfStories}
          />
        </div>

        <div data-field="squareFootage">
          <TextField
            label="Square Footage"
            type="number"
            min={1}
            step={1}
            numeric
            required
            value={state.squareFootage ?? ''}
            onChange={(e) => setField('squareFootage', intFromEvent(e))}
            onBlur={() => markTouched('squareFootage')}
            error={errors.squareFootage}
          />
        </div>

        <div data-field="numberOfUnits">
          <TextField
            label="Number of Units"
            type="number"
            min={1}
            step={1}
            numeric
            required
            value={state.numberOfUnits ?? ''}
            onChange={(e) => setField('numberOfUnits', intFromEvent(e))}
            onBlur={() => markTouched('numberOfUnits')}
            error={errors.numberOfUnits}
          />
        </div>

        <div data-field="projectDescription" className="md:col-span-2">
          <label
            htmlFor="project-description"
            className="block font-sans font-bold uppercase tracking-[0.125rem] text-[0.6875rem] text-[color:var(--color-ink)]/70 mb-1"
          >
            Project Description <span className="font-normal normal-case tracking-normal">(optional)</span>
          </label>
          <textarea
            id="project-description"
            rows={4}
            value={state.projectDescription ?? ''}
            onChange={(e) => setField('projectDescription', e.target.value)}
            onBlur={() => markTouched('projectDescription')}
            className="w-full bg-white border border-[color:var(--color-hairline)] hover:border-[color:var(--color-ink)]/25 rounded-sm px-3 py-2 text-[0.9375rem] focus:outline-none focus:border-[color:var(--color-blue)] transition-colors duration-200"
          />
        </div>
      </FieldGrid>

      {state.state === 'OTHER' && <OtherStatePanel />}
    </fieldset>
  );
}
